import React, { useEffect, useMemo, useState } from 'react';

import {
    addThousandsSeparator,
    formatDollarsLong,
    getCheapestPlan,
    locale,
} from '@trqk/utils';
import { IconInfoCircle } from '@trqk/components';
import cx from 'classnames';

import { calculatePlanPrice, getPlanDisplayName } from '../../utils';
import { detectionAddonId } from '../../constants';
import { ChoosePlanGapAnalysis, ChoosePlanSlider, HostedPageButton } from '.';
import { TopPlanFeatures } from '..';

interface ChoosePlanProps {
    chargebeeInstance: null | ChargebeeInstance;
    chargebeePlanSettings: ChargebeePlanSettings[] | null;
    onButtonViewed(): void;
    openCheckoutOptions: ChargebeeOpenCheckoutOptionsPartial;
    redirectUrl: string;
    subscriptionSettings: Maybe<PaymentPlanSubscriptionSettings>;
    width: number;
    usage: PaymentPlanUsage;
}

/**
 * Guides the user to choose a plan based upon their current royalties and detections.
 * @param props - {@link ChoosePlanProps}
 * @returns JSX
 */
export const ChoosePlan = ({
    chargebeeInstance,
    chargebeePlanSettings,
    onButtonViewed,
    openCheckoutOptions,
    redirectUrl,
    subscriptionSettings,
    width,
    usage,
}: ChoosePlanProps): JSX.Element | null => {
    const paymentPlan = useMemo(
        () => getCheapestPlan(chargebeePlanSettings, usage.royalties.used),
        [chargebeePlanSettings, usage.royalties.used],
    );

    const minDetectionsAllowed = paymentPlan?.DETECTION_TRACKS_INCLUDED ?? 0;
    // Although paymentPlan.DETECTION_TRACKS_MAX looks like a hard limit, it's really just a
    // placeholder number; we can charge the user for as many additional detections as they want to
    // purchase.  In the rare case that the user is using more detections than
    // paymentPlan.DETECTION_TRACKS_MAX, we just set their maxDetectionsAllowed to their current
    // number of detections used.
    const maxDetectionsAllowed = Math.max(
        usage.detectionTracks.used ?? 0,
        paymentPlan?.DETECTION_TRACKS_MAX ?? 1000,
    );
    const initialDetectionsAvailable = Math.max(
        usage.detectionTracks.used ?? 0,
        minDetectionsAllowed,
    );

    const [detectionsAvailable, setDetectionsAvailable] = useState(
        initialDetectionsAvailable,
    );

    useEffect(() => {
        // The paymentPlan can be null initially, so we need to make sure to update the state of
        // detectionsAvailable once we can read paymentPlan.DETECTION_TRACKS_INCLUDED
        setDetectionsAvailable(initialDetectionsAvailable);
    }, [initialDetectionsAvailable]);

    const [enableGapAnalysis, setEnableGapAnalysis] = useState(
        paymentPlan?.id !== 'CHARGEBEE_PLAN_SCALE',
    );

    const planTypeLabel = getPlanDisplayName(
        paymentPlan?.display_name,
        paymentPlan?.id ?? null,
    );

    const formattedRoyaltiesAvailable = addThousandsSeparator(
        paymentPlan?.ROYALTY_DOLLARS_INCLUDED ?? 0,
    );

    const planPrice = useMemo(
        () =>
            calculatePlanPrice({
                detectionsAvailable,
                enableGapAnalysis,
                planSettings: paymentPlan,
            }),
        [enableGapAnalysis, detectionsAvailable],
    );

    const planPriceFormatted = formatDollarsLong(planPrice);

    const planPriceLabel =
        planPrice === 0
            ? locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN.FREE
            : `${planPriceFormatted} ${locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.PER_QUARTER}`;

    if (paymentPlan === null) {
        return null;
    }

    let featureList: string[] = [];

    switch (paymentPlan?.id) {
        case 'CHARGEBEE_PLAN_SCALE':
            featureList =
                locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.FEATURE_LISTS.SCALE;
            break;
        default:
            featureList =
                locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.FEATURE_LISTS
                    .BASIC_TO_PRO;
    }

    const subscription_gap_analysis_bmat_sourced =
        subscriptionSettings?.subscription_gap_analysis_bmat_sourced ?? false;
    const subscription_gap_analysis_trqk_sourced =
        subscriptionSettings?.subscription_gap_analysis_trqk_sourced ?? false;

    return (
        <div className="border border-tertiary-200 rounded" style={{ width }}>
            <div className="flex flex-col justify-end border-b border-tertiary-200 mb-6 px-6">
                <h3 className="font-normal mt-6 text-xl">
                    {planTypeLabel.toUpperCase()}
                </h3>
                <div
                    className="mt-4 grid"
                    style={{ gridTemplateColumns: '24px auto' }}
                >
                    <IconInfoCircle
                        fillColor="secondary-500"
                        addClasses="mr-1"
                        width={20}
                    />
                    <p className="text-sm text-secondary-500 italic mb-4">
                        {
                            locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN
                                .CHOOSE_PLAN.WE_RECOMMEND
                        }
                    </p>
                </div>
            </div>
            <div className="mx-6 mt-6 border-b border-tertiary-200">
                <div className="text-sm font-semibold text-tertiary-400">
                    {
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.SETTINGS
                            .QUARTERLY_ROYALTIES
                    }
                </div>
                <div className="my-6 text-xl font-light">{`${locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN.UP_TO} $${formattedRoyaltiesAvailable}`}</div>
            </div>
            <div className="mx-6 mt-4 mb-6 border-b border-tertiary-200 pb-8">
                {initialDetectionsAvailable > -1 && maxDetectionsAllowed > -1 && (
                    <ChoosePlanSlider
                        currentValue={detectionsAvailable}
                        defaultValue={initialDetectionsAvailable}
                        minValueAllowed={minDetectionsAllowed}
                        label={
                            locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.SETTINGS
                                .DETECTION_TRACKS
                        }
                        sliderStepConfig={{
                            minValue: minDetectionsAllowed,
                            maxValue: maxDetectionsAllowed,
                            step: 1,
                        }}
                        paymentSettingType="DETECTIONS"
                        onChange={(value: number): void => {
                            setDetectionsAvailable(value);
                        }}
                        width={width}
                    />
                )}
            </div>
            <ChoosePlanGapAnalysis
                canUseBmatDetections={subscription_gap_analysis_bmat_sourced}
                canUseTrqkDetections={subscription_gap_analysis_trqk_sourced}
                checked={enableGapAnalysis}
                onChange={e => {
                    if (paymentPlan?.id === 'CHARGEBEE_PLAN_SCALE') {
                        const target = e.target as HTMLInputElement;
                        const { checked } = target;

                        setEnableGapAnalysis(checked);
                    }
                }}
            />
            <div className="m-6">
                <div
                    className={cx(
                        'mb-6 font-semibold text-secondary-500 text-center flex justify-center items-center',
                        { 'text-xl': planPrice === 0 },
                    )}
                    style={{
                        height: 24,
                    }}
                >
                    <p>{planPriceLabel}</p>
                </div>
                <div className="flex justify-center">
                    <HostedPageButton
                        addOns={[
                            {
                                add_on: detectionAddonId,
                                quantity: detectionsAvailable,
                            },
                        ]}
                        chargebeeInstance={chargebeeInstance}
                        label={
                            locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.LINKS
                                .CHOOSE_PLAN
                        }
                        onButtonViewed={onButtonViewed}
                        openCheckoutOptions={openCheckoutOptions}
                        redirectUrl={redirectUrl}
                        planId={paymentPlan?.id ?? null}
                    />
                </div>
                <TopPlanFeatures
                    list={featureList}
                    title={`${
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN
                            .TOP
                    } ${planTypeLabel.toUpperCase()} ${
                        locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.CHOOSE_PLAN
                            .FEATURES
                    }`}
                />
            </div>
        </div>
    );
};
